import { leadStateText } from "@/lib/campaign";
import {
  atLeast,
  type CadenceBody, type CadenceCall, type CadencePhase, type CadenceRedial, type CadenceRef, type Campaign,
  type CampaignStageCadence, type GreetingField, type LeadCadenceDecision, type LeadCadenceMembership,
  type MessageCadence, type OnVoicemail, type Role, type StageRow, type StepChannel,
} from "@/lib/types";
import { formatDateTime } from "./utils.js";
import { waitingFor } from "./waiting-for.js";
import { scriptPieces } from "./voicemail.js";

/**
 * A cadence as the console shows and edits it (ADR-066): the words for why a lead is due when it
 * is and why its priority is what it is, the buckets of a campaign's work, and the arithmetic
 * between the editor's form and the JSON body core-api validates (`services/shared/cadence_body.py`
 * in `platform`). The server decides every bound; the copies here keep a value it would refuse off
 * the form and say why before a round trip does. Pure, for `test/cadence.test.ts`.
 */

export type Tone = "neutral" | "accent" | "success" | "warning" | "danger" | "outline";

export interface Term {
  label: string;
  description: string;
}

// --- strategies -------------------------------------------------------------------------------------

export const DID_STRATEGIES = ["branded", "same_as_last", "unbranded", "rotate"] as const;

const STRATEGY: Record<string, Term> = {
  branded: { label: "Branded number", description: "The campaign's own caller ID." },
  same_as_last: { label: "Same as the last call", description: "The number the lead was last called from, so the callback reaches the same place." },
  unbranded: { label: "Local number", description: "Another number routed to this campaign: one in the lead's state when there is one, else any of the others, else the branded number (ADR-069)." },
  rotate: { label: "Rotating numbers", description: "The campaign's numbers in turn: the one used longest ago, never the number the lead was last called from while there is another (ADR-075)." },
};

export function strategyText(strategy: string): Term {
  return STRATEGY[strategy] ?? { label: strategy, description: "Not a strategy this console knows." };
}

/** What this build runs: every strategy since rotation runs (ADR-075). */
export const SUPPORTED_STRATEGIES: readonly string[] = ["branded", "same_as_last", "unbranded", "rotate"];

// --- why a lead is where it is --------------------------------------------------------------------

const NEXT_REASON: Record<string, string> = {
  enrolled: "Just enrolled",
  initial_delay: "Waiting out the initial delay",
  adopted: "Placed at the position its calls imply",
  between_calls: "The next call of the cycle",
  text_due: "A text is due",
  daily_texts: "Texted enough for today",
  after_cycle: "Waiting for the next cycle",
  after_conversation: "Spoke with a rep, waiting for the next cycle",
  disposition_wait: "The outcome's own wait",
  technical_retry: "Retrying after a technical failure",
  invalid_retry: "Retrying a number the carrier refused",
  not_worked: "Hung up while connecting, not charged",
  not_counted: "A call that did not count",
  callback: "A callback the rep promised",
  dropped: "The line dropped: kept for the rep to call back",
  stage_cadence: "Moved to the stage's cadence",
  hold: "Held",
  skipped: "Skipped by a rep",
  paused: "Taken back when every cadence was paused",
  expired_replanned: "Replanned to the next cycle",
  next_phase: "Waiting for the next phase",
  calling_window: "Waiting for the lead's calling window",
  callback_window: "Making the callback's calls inside its window",
  callback_elsewhere: "Waiting: their callback is worked on another list",
  sticky_redial: "Being redialed at once, for the same rep",
  rep_left: "The rep went away: the next free rep makes this call",
  rep_left_over: "Cut short when the rep went away: the call is made again",
  reenrolled: "Re-enrolled from the top",
  quiet_hours: "Outside the calling hours",
  on_a_call: "On a call",
  daily_cap: "Called enough for today",
  needs_disposition: "Waiting for the rep's outcome",
  total_attempts: "Out of attempts",
  expired: "The cadence has run its course",
  cadence_end: "Past the last phase",
  invalid_number: "The number is not in service",
  dnc: "Do not call",
  exit_stage: "Left the lifecycle",
  terminal: "Finished by the outcome",
  handed_over: "Handed to another list",
  no_consent: "No consent evidence",
  no_timezone: "No time zone can be known",
  retired: "Past the list horizon",
};

/** Why a row is due when it is. `channel` is what it waits for (ADR-135 in platform): the wait
 *  between two steps is for a text when a text is next. */
export function nextReasonText(reason: string | null | undefined, channel?: StepChannel | null): string | null {
  if (!reason) return null;
  if (channel === "sms" && reason === "between_calls") return "The next text of the cycle";
  return NEXT_REASON[reason] ?? reason.replaceAll("_", " ");
}

const PRIORITY_REASON: Record<string, string> = {
  callback: "A promised callback",
  sticky_redial: "A redial for the same rep",
  rep_left: "Passed on from a rep who went away",
  about_to_sign: "Near signing",
  mid_cycle: "The second call of a double dial",
  initial_fresh: "Never reached yet",
};

/** `initial_d3` is the phase's band on day 3; `followup` the phase's flat band. */
export function priorityReasonText(reason: string | null | undefined): string | null {
  if (!reason) return null;
  if (PRIORITY_REASON[reason]) return PRIORITY_REASON[reason];
  const aged = /^(.+)_d(\d+)$/.exec(reason);
  if (aged) return `Day ${aged[2]} of the ${(aged[1] ?? "").replaceAll("_", " ")} phase`;
  return `The ${reason.replaceAll("_", " ")} phase`;
}

export function positionText(cycle: number, step: number, channel?: StepChannel | null): string {
  return `Cycle ${cycle}, ${channel === "sms" ? "text" : "call"} ${step + 1} next`;
}

// --- the lead's place, as the record's Follow-up card shows it -----------------------------------

const HOLD: Record<string, string> = {
  "cadence:paused": "Paused by an admin",
  "firm:paused": "The firm is paused",
  appointment: "An appointment is booked",
  appointment_set: "An appointment is booked",
  contract_review: "A contract review is booked",
  hold: "A hold outcome",
};

/** Why a hold keeps the lead off the list, in words; a reason this console does not know reads as
 *  itself. */
export function holdText(heldBy: string): string {
  if (HOLD[heldBy]) return HOLD[heldBy];
  const words = heldBy.replaceAll(":", " ").replaceAll("_", " ").trim();
  return words ? words[0]!.toUpperCase() + words.slice(1) : "Held";
}

export type StatusKind = "due" | "overdue" | "held" | "on_call" | "needs_outcome" | "rest";

/** The headline of a lead's place on a cadence campaign: what the dialer will do with it next. */
export interface CadenceStatus {
  kind: StatusKind;
  tone: Tone;
  /** "Due in 3 h 58 min", "Held until …", "Finished by the outcome". */
  headline: string;
  /** The reason under it. */
  detail: string | null;
  /** The instant the headline speaks of, for a tooltip; null when there is none. */
  at: string | null;
  /** When the next call is due, where that is not `at`: Text first's `at` is the end of its 24
   *  hours, and a retry can put the row later (ACL-400). */
  nextCall?: string | null;
}

/** Due for longer than this with nobody taking it is overdue: the campaign page's bucket. */
export const OVERDUE_AFTER_S = 15 * 60;

const AT_REST: ReadonlySet<string> = new Set(["done", "dnc", "exhausted", "retired", "blocked"]);
const REST_TONE: Record<string, Tone> = {
  done: "success", dnc: "danger", blocked: "danger", exhausted: "neutral", retired: "neutral",
};

/** `textFirstUntil` is the lead's own `text_first_until`, the end of its Text first. */
export function cadenceStatus(
  m: LeadCadenceMembership,
  now: number = Date.now(),
  textFirstUntil: string | null = null,
): CadenceStatus {
  // A row the dialer would work but for SMS only or Text first (ACL-344): it stays where it is, and
  // is not due.
  if (m.dial === "sms_only") {
    return {
      kind: "held",
      tone: "warning",
      headline: "SMS only",
      detail: "Asked to be texted, not called. Nothing dials them until they are set back to calls and texts.",
      at: null,
    };
  }
  if (m.dial === "text_first") {
    // The end comes from the lead, never from `next_at`: that is when the row is next due, the
    // latest of a hold, a retry and the end, so a retry can put it later (ACL-400). Without a
    // known end the headline names no date rather than a wrong one.
    const ends = textFirstUntil && !Number.isNaN(Date.parse(textFirstUntil)) ? textFirstUntil : null;
    const due = m.next_at && (!ends || Date.parse(m.next_at) > Date.parse(ends)) ? m.next_at : null;
    const wait = "Texted first: the dialer waits until the 24 hours are over, then the cadence carries on.";
    return {
      kind: "held",
      tone: "warning",
      headline: ends ? `Text first until ${formatDateTime(ends)}` : "Text first",
      detail: due && formatDateTime(due) !== formatDateTime(ends) ? `${wait} The next call is due ${formatDateTime(due)}.` : wait,
      at: ends,
      nextCall: due ?? ends,
    };
  }
  if (m.held_by) {
    return {
      kind: "held",
      tone: "neutral",
      headline: m.next_at ? `Held until ${formatDateTime(m.next_at)}` : "Held",
      detail: holdText(m.held_by),
      at: m.next_at,
    };
  }
  if (AT_REST.has(m.state)) {
    const state = leadStateText(m.state);
    return {
      kind: "rest",
      tone: REST_TONE[m.state] ?? "neutral",
      headline: nextReasonText(m.next_reason) ?? state.label,
      detail: state.description,
      at: null,
    };
  }
  // Before "on a call": the dialer keeps the row `in_flight` after a call a rep took is over,
  // until the rep's outcome comes, and core-api says so with `needs_outcome` (ACL-486).
  if (awaitsOutcome(m)) {
    return {
      kind: "needs_outcome",
      tone: "warning",
      headline: waitingFor(m.outcome_rep),
      detail: "The call ended without an outcome. The cadence moves on once a rep records one.",
      at: null,
    };
  }
  if (m.state === "offered" || m.state === "in_flight") {
    return {
      kind: "on_call",
      tone: "accent",
      headline: "On a call",
      detail: m.assigned_rep ? `With ${m.assigned_rep}` : leadStateText(m.state).description,
      at: null,
    };
  }
  const why = nextReasonText(m.next_reason, m.next_channel);
  const at = m.next_attempt_at;
  const delta = at ? Math.round((Date.parse(at) - now) / 1000) : 0;
  if (!at || Math.abs(delta) < 45) return { kind: "due", tone: "accent", headline: "Due now", detail: why, at };
  if (delta > 0 || -delta <= OVERDUE_AFTER_S) {
    return { kind: "due", tone: "accent", headline: `Due ${relativeSpan(at, now)}`, detail: why, at };
  }
  return { kind: "overdue", tone: "warning", headline: `Due ${relativeSpan(at, now)}`, detail: why, at };
}

/** When a lead's next call on a campaign is due, and nothing else (ACL-575): the contact page
 *  shows nobody the cadence, its phases or why. */
export interface CallDue {
  /** `due` has a time to come, or is due now; the other two have none. */
  kind: "due" | "on_call" | "awaiting_outcome";
  /** "Due in 2 h", "Due now", "On a call now". */
  text: string;
  /** The instant it is due; null while on a call, waiting for the outcome, or due now with no time. */
  at: string | null;
  overdue: boolean;
}

/** The row's next call, as `cadenceStatus` reads the row, so the contact page and the campaign's
 *  pages never disagree on when a lead is due; null when none is coming: SMS only, a hold with no
 *  end, or a row at rest. */
export function callDue(
  m: LeadCadenceMembership,
  now: number = Date.now(),
  textFirstUntil: string | null = null,
): CallDue | null {
  const status = cadenceStatus(m, now, textFirstUntil);
  switch (status.kind) {
    case "rest":
      return null;
    case "needs_outcome":
      return { kind: "awaiting_outcome", text: "Waiting for the outcome", at: null, overdue: false };
    case "on_call":
      return { kind: "on_call", text: "On a call now", at: null, overdue: false };
    case "held": {
      // Its headline names the hold; the call is due when it ends.
      const at = status.nextCall ?? status.at;
      return at ? { kind: "due", text: `Due ${relativeSpan(at, now)}`, at, overdue: false } : null;
    }
    case "due":
    case "overdue":
      return { kind: "due", text: status.headline, at: status.at, overdue: status.kind === "overdue" };
  }
}

/** Whether the reader is told when the lead is called next (ACL-642): the Next call card, the line
 *  under the record's name and the callback pinned above the timeline. An admin and up; a rep or a
 *  supervisor works thousands of leads and the dialer decides when each is called. The cadence and
 *  the callback run the same whoever reads the record. */
export function seesNextCall(role: Role | undefined): boolean {
  return atLeast(role, "admin");
}

/** Whether the row waits for a rep's outcome: parked for one, or held `in_flight` for a call
 *  that is over (core-api's `needs_outcome`, ACL-486). */
export function awaitsOutcome(m: Pick<LeadCadenceMembership, "state" | "dial">): boolean {
  return m.state === "needs_disposition" || m.dial === "needs_outcome";
}

/** A state the dialer never calls again until a person or a list recycle moves it. */
export function atRest(state: string): boolean {
  return AT_REST.has(state);
}

/** Where in the cycle, said for the tile: what is next while the row still dials, what was made
 *  once it rests. */
export function cycleWords(m: LeadCadenceMembership): string {
  if (atRest(m.state)) {
    if (m.step === 0) return "no call made";
    return `${m.step} call${m.step === 1 ? "" : "s"} made`;
  }
  return `${m.next_channel === "sms" ? "text" : "call"} ${m.step + 1} next`;
}

/** "day 3" from an aged phase band; null when the priority says something else. */
export function phaseDay(priorityReason: string | null | undefined): string | null {
  const aged = /^(.+)_d(\d+)$/.exec(priorityReason ?? "");
  return aged ? `day ${aged[2]}` : null;
}

const DECISION: Record<string, string> = {
  claimed: "Claimed for a call",
  enrolled: "Enrolled",
  adopted: "Adopted",
  after_call: "After the call",
  after_disposition: "After the outcome",
  recheck: "Rechecked",
  reaped: "Taken back by the dialer",
  late_outcome: "A late outcome",
  reenrolled: "Started again from the top",
  reanchored: "Re-anchored",
  redials_over: "The redials ended",
  rep_left: "Passed to a free rep",
  rep_left_over: "No rep took it in time",
  reopened: "Reopened: carries on where it stopped",
  rezoned: "Read again on the lead's corrected clock",
  blocked: "Blocked",
  released: "Released",
  shadow: "Shadow decision",
  malformed: "Cadence not readable",
  sms_step: "Text asked for",
  sms_skipped: "Text passed over",
};

const DECISION_TONE: Record<string, Tone> = {
  claimed: "accent", enrolled: "accent", adopted: "accent", reenrolled: "accent", reanchored: "accent",
  reopened: "accent",
  reaped: "warning", released: "warning", blocked: "danger", malformed: "danger", shadow: "outline",
};

export interface DecisionWords {
  title: string;
  /** The reason, the state it left the row in, and how long until the next call. */
  detail: string | null;
  tone: Tone;
}

/** The words a caller lends `decisionText` for the things it cannot know on its own: a stage's
 *  label from its slug, a cadence's name from its `slug@version` label. */
export interface DecisionNames {
  stage?: (slug: string) => string;
  cadence?: (label: string) => string;
}

/** The stage a stage switch was for: its detail, else its trigger key (`stage_cadence:<stage>:<at>`). */
function switchedStage(d: LeadCadenceDecision): string | null {
  const named = d.detail?.stage;
  if (typeof named === "string" && named) return named;
  const key = /^stage_cadence:([^:]+):/.exec(d.trigger_key ?? "");
  return key?.[1] || null;
}

/** One decision of the dialer's, in words a rep reads: what it did, why, and what it left. */
export function decisionText(d: LeadCadenceDecision, names: DecisionNames = {}): DecisionWords {
  if (d.reason === "stage_cadence") return stageSwitchText(d, names);
  const title = DECISION[d.kind] ?? (d.kind ? d.kind[0]!.toUpperCase() + d.kind.slice(1).replaceAll("_", " ") : "Decision");
  const parts: string[] = [];
  if (d.reason && d.reason !== d.kind) {
    const known = NEXT_REASON[d.reason];
    parts.push(known ?? d.reason[0]!.toUpperCase() + d.reason.slice(1).replaceAll("_", " "));
  } else if (d.kind === "adopted") {
    parts.push(NEXT_REASON.adopted!);
  }
  if (d.to_state && d.to_state !== d.from_state) parts.push(`now ${leadStateText(d.to_state).label.toLowerCase()}`);
  if (d.next_attempt_at) {
    const wait = Math.round((Date.parse(d.next_attempt_at) - Date.parse(d.at)) / 1000);
    parts.push(wait <= 0 ? "next call right away" : `next call in ${spanText(wait)}`);
  }
  let tone: Tone = DECISION_TONE[d.kind] ?? "neutral";
  if (d.to_state === "done") tone = "success";
  else if (d.to_state === "dnc" || d.to_state === "blocked") tone = "danger";
  return { title, detail: parts.join(" · ") || null, tone };
}

/** A stage switch (ADR-087): the stage the lead moved to, the cadence the row joined, and when
 *  that cadence calls first. The row is re-enrolled, but "started again from the top" would read
 *  as a reset, and the move is the fact. */
function stageSwitchText(d: LeadCadenceDecision, names: DecisionNames): DecisionWords {
  const stage = switchedStage(d);
  const title = stage ? `Stage moved to ${names.stage ? names.stage(stage) : stage.replaceAll("_", " ")}` : "Stage moved";
  const parts: string[] = [];
  const to = d.detail?.to;
  if (typeof to === "string" && to) parts.push(`now on the ${cadenceLabel(to, names.cadence)} cadence`);
  if (d.next_attempt_at) {
    const wait = Math.round((Date.parse(d.next_attempt_at) - Date.parse(d.at)) / 1000);
    parts.push(wait <= 0 ? "first call right away" : `first call in ${spanText(wait)}`);
  }
  return { title, detail: parts.join(" · ") || null, tone: "accent" };
}

/** A cadence's name from its `slug@version` label, through the caller's lookup when it has one,
 *  else the slug in words with the version dropped: "sign_callback@1" reads "sign callback". */
export function cadenceLabel(label: string, lookup?: (label: string) => string): string {
  if (lookup) return lookup(label);
  const [slug = ""] = label.split("@");
  return slug.replaceAll("_", " ") || label;
}

/** The Follow-up card's line under a row that runs a stage's cadence rather than the campaign's
 *  (ADR-087): whose cadence it is, since when, and what it ran before. Null on the campaign's own
 *  cadence, which the card names in its header already. */
export function stageCadenceLine(
  m: LeadCadenceMembership,
  names: DecisionNames = {},
  now: number = Date.now(),
): string | null {
  if (!m.cadence || !m.cadence_stage) return null;
  const stage = names.stage ? names.stage(m.cadence_stage) : m.cadence_stage.replaceAll("_", " ");
  const parts = [`${stage}'s cadence`];
  // The decision that put the row on this run of the cadence: the stage switch, or the restart
  // a finished row got when the lead moved to a stage the campaign works (both re-enrol).
  const moved = m.decisions.find((d) => d.reason === "stage_cadence" || d.kind === "reenrolled" || d.kind === "enrolled");
  if (moved) {
    parts.push(`on it since ${relativeSpan(moved.at, now)}`);
    const from = moved.detail?.from;
    if (typeof from === "string" && from) parts.push(`before that, ${cadenceLabel(from, names.cadence)}`);
  }
  return parts.join(" · ");
}

// --- a text a cadence sent (ADR-135 in platform) ---------------------------------------------------

/** What the "Cadence" mark on a text says under the pointer: which cadence sent it and as which
 *  step. The step is named as the cadence page names it, by its place in the round. */
export function sentByCadenceText(c: MessageCadence): string {
  const name = c.name ?? cadenceLabel(c.cadence);
  return `Sent by the ${name} cadence, as Text ${c.step} of cycle ${c.cycle}. Nobody sent it by hand.`;
}

/** Who sent a cadence's text, in a line of details beside others: "Cadence: no answer, Text 2". */
export function cadenceSender(c: MessageCadence): string {
  return `Cadence: ${c.name ?? cadenceLabel(c.cadence)}, Text ${c.step}`;
}

const REQUEST: Record<string, string> = {
  pause: "Pause",
  resume: "Resume",
  reenroll: "Start again from the top",
  reanchor: "Re-anchor",
  reopen: "Back on the list after a reopen",
};

/** What an admin asked the dialer for, by the request's kind. */
export function requestText(kind: string): string {
  return REQUEST[kind] ?? kind.replaceAll("_", " ");
}

/** How often a lead's cadence view refreshes on its own, and how often while it waits. */
export const CADENCE_POLL_MS = 30_000;
export const CADENCE_WAITING_POLL_MS = 3_000;

/**
 * How soon to read a lead's cadence again. While a request waits for the dialer (a re-enroll, or
 * an outcome that hands the lead back to a list) the view still shows where the lead stood
 * before it: the old cadence's phase and step. The dialer applies a request within seconds, so
 * the view asks again every few seconds until none is waiting, instead of leaving the old phase
 * up for half a minute.
 */
export function leadCadencePoll(
  data: { memberships: readonly { pending_requests: readonly unknown[] }[] } | undefined,
): number {
  const waiting = data?.memberships.some((m) => m.pending_requests.length > 0) ?? false;
  return waiting ? CADENCE_WAITING_POLL_MS : CADENCE_POLL_MS;
}

// --- the buckets ------------------------------------------------------------------------------------

export const BUCKETS = [
  "due", "overdue", "held", "technical", "blocked", "needs_disposition", "exhausted", "double_membership",
] as const;
export type Bucket = (typeof BUCKETS)[number];

const BUCKET: Record<Bucket, Term & { tone: Tone }> = {
  due: { label: "Due", description: "Dialable now, waiting for a free rep.", tone: "accent" },
  overdue: { label: "Overdue", description: "Due for more than fifteen minutes with nobody taking it.", tone: "warning" },
  held: { label: "Held", description: "A hold keeps it: a pause, a booking, a hold outcome.", tone: "neutral" },
  technical: { label: "Technical", description: "Retrying after a failure of ours or the carrier's.", tone: "warning" },
  blocked: { label: "Blocked", description: "Do not call, or no consent evidence.", tone: "danger" },
  needs_disposition: { label: "Needs outcome", description: "A rep took the call and has not said what happened.", tone: "warning" },
  exhausted: { label: "Finished", description: "Out of attempts, or past the cadence's end.", tone: "neutral" },
  double_membership: { label: "On two lists", description: "Dialable on this campaign and another: two lists could call one person.", tone: "danger" },
};

export function bucketText(bucket: string): Term & { tone: Tone } {
  return BUCKET[bucket as Bucket] ?? { label: bucket, description: "", tone: "outline" };
}

// --- spans -----------------------------------------------------------------------------------------

/** "4 h", "90 s", "3 d", "1 h 30 min", "right away": how a wait reads on the page. */
export function spanText(seconds: number): string {
  if (seconds <= 0) return "right away";
  if (seconds < 60) return `${seconds} s`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (hours < 48) return rest ? `${hours} h ${rest} min` : `${hours} h`;
  const days = Math.floor(hours / 24);
  const hoursLeft = hours % 24;
  return hoursLeft ? `${days} d ${hoursLeft} h` : `${days} d`;
}

/** "in 3 h 58 min", "2 min ago". */
export function relativeSpan(iso: string, now: number = Date.now()): string {
  const delta = Math.round((new Date(iso).getTime() - now) / 1000);
  if (Math.abs(delta) < 45) return delta >= 0 ? "now" : "just now";
  return delta > 0 ? `in ${spanText(delta)}` : `${spanText(-delta)} ago`;
}

/** One line for a lead's place on a cadence campaign: when, why, and where in the cycle. */
export function cadenceNextLine(m: LeadCadenceMembership, now: number = Date.now()): string {
  const where = m.cadence ? `${positionText(m.cycle, m.step, m.next_channel)}` : null;
  const why = nextReasonText(m.next_reason, m.next_channel);
  if (m.held_by) {
    const until = m.next_at ? ` until ${formatDateTime(m.next_at)}` : "";
    const reason = m.held_by === "cadence:paused" ? "paused" : m.held_by.replaceAll("_", " ");
    return [`Held${until} (${reason})`, where].filter(Boolean).join(" · ");
  }
  if (m.state === "exhausted" || m.state === "done" || m.state === "dnc" || m.state === "retired" || m.state === "blocked") {
    return [why ?? m.state, where].filter(Boolean).join(" · ");
  }
  if (awaitsOutcome(m)) return [waitingFor(m.outcome_rep), where].filter(Boolean).join(" · ");
  if (m.state === "offered" || m.state === "in_flight") {
    return ["On a call", where].filter(Boolean).join(" · ");
  }
  const when = m.next_attempt_at ? `Due ${relativeSpan(m.next_attempt_at, now)}` : "Due now";
  // A row on a stage's cadence rather than the campaign's says which (ADR-087).
  const on = m.cadence && m.cadence_stage ? `on the ${m.cadence_name ?? cadenceLabel(m.cadence)} cadence` : null;
  return [when, why, on, where].filter(Boolean).join(" · ");
}

/** A cycle in a phrase: "2 calls 1 min apart, then 4 h"; "3 calls back to back, then 4 h". */
export function cycleText(phase: CadencePhase): string {
  const c = phase.cycle;
  const texts = (c.calls ?? []).slice(0, c.attempts).filter((s) => s.channel === "sms").length;
  if (texts > 0) {
    // A cycle with a text in it is said by its counts: the waits between a text and a call are
    // the cadence page's to show.
    const dials = c.attempts - texts;
    const parts = [
      `${texts} text${texts === 1 ? "" : "s"}`,
      ...(dials > 0 ? [`${dials} call${dials === 1 ? "" : "s"}`] : []),
    ];
    return `${parts.join(" and ")}, then ${spanText(c.after_cycle_seconds)}`;
  }
  const calls = c.attempts === 1 ? "1 call" : `${c.attempts} calls`;
  const first = c.between_attempts_seconds?.[0] ?? 0;
  const between =
    c.attempts > 1 && c.between_attempts_seconds?.length
      ? first <= 0 ? " back to back" : ` ${spanText(first)} apart`
      : "";
  return `${calls}${between}, then ${spanText(c.after_cycle_seconds)}`;
}

/** The body in one line for a list: each phase's cycle and when it ends. */
export function bodySummary(body: CadenceBody): string {
  return body.phases
    .map((p) => {
      const ends = p.ends_days_after_enrollment ? `to day ${p.ends_days_after_enrollment}` : "open-ended";
      return `${p.name}: ${cycleText(p)} (${ends})`;
    })
    .join(" · ");
}

/**
 * A cadence's limits on one line of the list: its calls in all and its last day. A cadence with
 * no call in it spends no attempt (ADR-135 in platform), so it says the day's texts in their place.
 */
export function limitsLine(body: CadenceBody): string {
  const limits = body.limits;
  if (!limits) return "—";
  const days = limits.expire_days_after_enrollment ? `${limits.expire_days_after_enrollment} days` : "no expiry";
  const values = formFromBody(body);
  if (hasCalls(values)) return `${limits.total_attempts ?? "—"} attempts · ${days}`;
  const cap = values.daily_texts_per_lead.trim();
  return `${cap} text${cap === "1" ? "" : "s"} a day · ${days}`;
}

// --- a cadence per stage (ADR-087) ---------------------------------------------------------------

/** One row of the campaign page's stage table: what the stage runs, and where that comes from. */
export interface StageCadenceRow {
  stage: StageRow;
  /** The cadence the stage runs; null for none. */
  cadence: CadenceRef | null;
  /** `default`, the campaign's own; `own`, the campaign's table; `none`, an exit the campaign
   *  does not work; `missing`, the table names a cadence that no longer exists. */
  source: "default" | "own" | "none" | "missing";
}

/** The table, in the catalog's order: a stage the campaign lists runs its own cadence, an exit
 *  it does not list runs none (the lead leaves the list), everything else runs the default. */
export function stageCadenceRows(stages: readonly StageRow[], campaign: Pick<Campaign, "cadence" | "stage_cadences">): StageCadenceRow[] {
  const own = new Map((campaign.stage_cadences ?? []).map((s) => [s.stage, s]));
  return stages.map((stage) => {
    const listed = own.get(stage.slug);
    if (listed) return { stage, cadence: listed.cadence, source: listed.cadence ? "own" : "missing" };
    if (stage.group === "exit") return { stage, cadence: null, source: "none" };
    return { stage, cadence: campaign.cadence, source: "default" };
  });
}

/** `PUT /campaigns/{id}/stage-cadences` takes the whole set: the table with one stage's cadence
 *  set, changed or removed (`cadenceId` null). Stages keep the catalog's order. */
export function stageCadenceItems(
  current: readonly CampaignStageCadence[],
  stage: string,
  cadenceId: number | null,
  order: readonly string[] = [],
): { stage: string; cadence_id: number }[] {
  const items = new Map(current.map((s) => [s.stage, s.cadence_id]));
  if (cadenceId === null) items.delete(stage);
  else items.set(stage, cadenceId);
  const rank = new Map(order.map((slug, i) => [slug, i]));
  return [...items.entries()]
    .map(([s, cadence_id]) => ({ stage: s, cadence_id }))
    .sort((a, b) => (rank.get(a.stage) ?? order.length) - (rank.get(b.stage) ?? order.length));
}

/** When a cadence calls a lead first: "at once", or its first phase's delay, in whole days when
 *  it is one ("1 d on", "3 d on"), else as a span ("1 h 30 min on"). */
export function firstCallText(body: CadenceBody | null | undefined): string {
  const delay = body?.phases[0]?.cycle.initial_delay_seconds ?? 0;
  if (delay <= 0) return "at once";
  const day = 24 * 60 * 60;
  return delay % day === 0 ? `${delay / day} d on` : `${spanText(delay)} on`;
}

/** How a cadence goes on after that: its first phase's cycle, and when the whole thing ends. */
export function thenText(body: CadenceBody | null | undefined): string {
  const first = body?.phases[0];
  if (!first) return "—";
  const ends = body?.limits?.expire_days_after_enrollment;
  return ends ? `${cycleText(first)} · ends after ${ends} day${ends === 1 ? "" : "s"}` : cycleText(first);
}

// --- the editor ------------------------------------------------------------------------------------

/** The texts a cadence sends one number in a day when it does not say. A cadence always has a cap
 *  on texts: no rep has to be free for a text, so nothing else paces one (ADR-135 in platform). */
export const DEFAULT_DAILY_TEXTS = 2;

/** The bounds `cadence_body.py` enforces, in the units the form asks for. */
export const CADENCE_BOUNDS = {
  wait_minutes: [1, 30 * 24 * 60],
  /** Between two calls of one cycle: 0 is right away (ADR-069). */
  between_minutes: [0, 30 * 24 * 60],
  initial_delay_minutes: [0, 7 * 24 * 60],
  attempts: [1, 20],
  total_attempts: [1, 100],
  expire_days: [1, 365],
  /** Calls to one number in a day (ADR-067); the same ceiling as a cycle's calls. */
  daily_calls_per_number: [1, 20],
  /** Texts a cadence sends one number in a day (ADR-135). */
  daily_texts_per_lead: [1, 10],
  /** A cycle with no call in it starts again after this at least: nothing else paces a text. */
  text_cycle_minutes: [60, 30 * 24 * 60],
  priority: [0, 999],
  decay: [0, 100],
  technical_retry_minutes: [1, 24 * 60],
  max_retries: [0, 10],
  invalid_stops_after: [1, 10],
  not_worked_minutes: [1, 24 * 60],
  /** A callback kept for its rep: a minute to a day (ADR-157). */
  callback_kept_for_minutes: [1, 24 * 60],
  /** A callback worked for this long after its time: a minute to a day (ACL-661). */
  callback_window_minutes: [1, 24 * 60],
  phases: [1, 6],
} as const;

export const CADENCE_SLUG_RE = /^[a-z][a-z0-9_]{1,79}$/;
export const PHASE_NAME_RE = /^[a-z][a-z0-9_]{0,23}$/;

/** The most redials one call may have (ADR-103). */
export const MAX_REDIALS = 3;

/**
 * How long the dialer waits before a message it leaves by itself (ADR-109 in platform): the
 * seconds from the moment it hears the voicemail. It does not hear the beep a voicemail box
 * records from, so the wait stands in for the greeting. Ten when the cadence gives none, sixty
 * at most, and the page offers the usual ones.
 */
export const DEFAULT_VOICEMAIL_AFTER_SECONDS = 10;
export const MAX_VOICEMAIL_AFTER_SECONDS = 60;
export const VOICEMAIL_AFTER_PRESETS: readonly number[] = [5, 10, 15, 20, 30, 45, 60];

export interface RedialValues {
  did_strategy: string;
  on_voicemail: OnVoicemail;
  /** A voicemail library slug; "" for the message for the lead's stage and language. */
  voicemail_message: string;
  /** Read only where the dialer leaves the message by itself. */
  voicemail_after_seconds: number;
}

/** One step's own options, beside its wait and, for a call, its number. */
export interface CallValues {
  /** A call a rep takes, or a text nobody has to be free for (ADR-135 in platform). */
  channel: StepChannel;
  /** Made in the first round of its phase only. */
  only_once: boolean;
  on_voicemail: OnVoicemail;
  voicemail_message: string;
  voicemail_after_seconds: number;
  redials: RedialValues[];
  /** A text's words, with fields; "" on a call. */
  text: string;
  /** The same in Spanish; "" for none. */
  text_es: string;
}

export const NEW_REDIAL: RedialValues = {
  did_strategy: "same_as_last",
  on_voicemail: "hang_up",
  voicemail_message: "",
  voicemail_after_seconds: DEFAULT_VOICEMAIL_AFTER_SECONDS,
};
export const DEFAULT_CALL: CallValues = {
  channel: "call",
  only_once: false,
  on_voicemail: "hang_up",
  voicemail_message: "",
  voicemail_after_seconds: DEFAULT_VOICEMAIL_AFTER_SECONDS,
  redials: [],
  text: "",
  text_es: "",
};
/** A text as it is added: its words are the admin's to write. */
export const NEW_TEXT: CallValues = { ...DEFAULT_CALL, channel: "sms" };

/** The most characters a text's words may be, fields as they are written (`cadence_body.TEXT_MAX`). */
export const TEXT_MAX = 1000;
/** The most a field's fallback words may be (`greetings.FALLBACK_MAX`). */
export const TEXT_FALLBACK_MAX = 60;

export const isText = (step: Pick<CallValues, "channel">): boolean => step.channel === "sms";

/** "Call 2", "Text 1": a step by what it is and its place in the cycle, from 0. A step keeps its
 *  number whatever is around it, as the dialer's own count of a round does. */
export function stepName(step: Pick<CallValues, "channel">, index: number): string {
  return `${isText(step) ? "Text" : "Call"} ${index + 1}`;
}

/** Whether the choice leaves a message, whoever leaves it. */
export const leavesMessage = (choice: OnVoicemail): boolean => choice !== "hang_up";

/** "at once", "after 1 second", "after 10 seconds". */
export function afterWords(seconds: number): string {
  if (seconds <= 0) return "at once";
  return `after ${seconds} second${seconds === 1 ? "" : "s"}`;
}

/** The waits to offer: the usual ones, and the cadence's own when it is none of them. */
export function afterOptions(current: number): number[] {
  return VOICEMAIL_AFTER_PRESETS.includes(current)
    ? [...VOICEMAIL_AFTER_PRESETS]
    : [...VOICEMAIL_AFTER_PRESETS, current].sort((a, b) => a - b);
}

const copyCall = (c: CallValues): CallValues => ({ ...c, redials: c.redials.map((r) => ({ ...r })) });

/** The call list resized to a cycle's calls: a new call gets the defaults. */
export function callsFor(attempts: number, calls: readonly CallValues[]): CallValues[] {
  const wanted = Math.max(1, attempts);
  return Array.from({ length: wanted }, (_, k) => copyCall(calls[k] ?? DEFAULT_CALL));
}

/** Whether a step is a call at its defaults: made every round, hangs up on a voicemail, no
 *  redials. A text never is. */
export function isDefaultCall(c: CallValues): boolean {
  return !isText(c) && !c.only_once && c.on_voicemail === "hang_up" && c.redials.length === 0;
}

function redialFromBody(r: CadenceRedial): RedialValues {
  return {
    did_strategy: r.did_strategy ?? "same_as_last",
    on_voicemail: r.on_voicemail ?? "hang_up",
    voicemail_message: r.voicemail_message ?? "",
    voicemail_after_seconds: r.voicemail_after_seconds ?? DEFAULT_VOICEMAIL_AFTER_SECONDS,
  };
}

function callFromBody(c: CadenceCall | undefined): CallValues {
  if (!c) return copyCall(DEFAULT_CALL);
  return {
    channel: c.channel === "sms" ? "sms" : "call",
    only_once: c.only_once ?? false,
    on_voicemail: c.on_voicemail ?? "hang_up",
    voicemail_message: c.voicemail_message ?? "",
    voicemail_after_seconds: c.voicemail_after_seconds ?? DEFAULT_VOICEMAIL_AFTER_SECONDS,
    redials: (c.redials ?? []).map(redialFromBody),
    text: c.text ?? "",
    text_es: c.text_es ?? "",
  };
}

/** What an attempt writes about the message it leaves: which, for either choice that leaves one,
 *  and the wait only where the dialer leaves it by itself. */
function voicemail(a: { on_voicemail: OnVoicemail; voicemail_message: string; voicemail_after_seconds: number }) {
  return {
    ...(leavesMessage(a.on_voicemail) && a.voicemail_message.trim() ? { voicemail_message: a.voicemail_message.trim() } : {}),
    ...(a.on_voicemail === "leave_message_auto" ? { voicemail_after_seconds: a.voicemail_after_seconds } : {}),
  };
}

function callToBody(c: CallValues): CadenceCall {
  // A text carries its words and nothing a call has: core-api refuses one with redials or a
  // voicemail choice.
  if (isText(c)) {
    return {
      channel: "sms",
      only_once: c.only_once,
      text: c.text.trim(),
      ...(c.text_es.trim() ? { text_es: c.text_es.trim() } : {}),
    };
  }
  return {
    only_once: c.only_once,
    on_voicemail: c.on_voicemail,
    ...voicemail(c),
    redials: c.redials.map((r) => ({
      did_strategy: r.did_strategy,
      on_voicemail: r.on_voicemail,
      ...voicemail(r),
    })),
  };
}

export interface PhaseValues {
  name: string;
  /** "" for an open-ended last phase. */
  ends_days: string;
  priority_band: number;
  age_decay_per_day: number;
  /** "" for never. */
  not_after_minutes: string;
  attempts: number;
  between_minutes: number[];
  after_cycle_minutes: number;
  initial_delay_minutes: number;
  did_strategy: string[];
  /** One per step of the cycle (ADR-103): a call, or a text (ADR-135). */
  calls: CallValues[];
  /** "Start rounds in the lead's calling window" (ADR-156); absent is off. */
  prefer_window?: boolean;
}

export interface CadenceFormValues {
  name: string;
  slug: string;
  note: string;
  phases: PhaseValues[];
  total_attempts: number;
  /** "" for never. */
  expire_days: string;
  /** At most this many calls to one number a day, from any campaign, a hand dial too; "" for no cap. */
  daily_calls_per_number: string;
  /** At most this many texts from cadences to one number a day, 1 to 10; there is no "no cap"
   *  for texts (ADR-135). */
  daily_texts_per_lead: string;
  /** The outcome the cadence sets when it runs out (ADR-155); "" for none. */
  end_outcome: string;
  technical_retry_minutes: number;
  max_retries: number;
  invalid_stops_after: number;
  not_worked_minutes: number;
  abandoned_counts: boolean;
  manual_dial_counts: boolean;
  /** A rep's outcome on a call the lead made spends an attempt (ADR-071). */
  inbound_counts: boolean;
  callback_default_minutes: number;
  /** How long a callback waits for the rep who promised it before any rep may make it, 1 minute
   *  to a day (ADR-157 in platform). */
  callback_kept_for_minutes: number;
  /** How long a callback is worked after its time with the phase's own calls, redials and waits
   *  (ACL-661); null for just its call and redials, which is how a cadence without it behaves. */
  callback_window_minutes: number | null;
  priority_callback: number;
  priority_about_to_sign: number;
  priority_mid_cycle: number;
  priority_initial_fresh: number;
}

/** The client's default: two calls a minute apart, four hours between cycles, one call every
 *  three days after two weeks (the meeting's numbers, taken as the default on 2026-09-25), never
 *  more than six calls to a number in a day (ADR-103: a call and two redials fit twice). */
export const NEW_CADENCE: CadenceFormValues = {
  name: "",
  slug: "",
  note: "",
  phases: [
    {
      name: "initial", ends_days: "14", priority_band: 400, age_decay_per_day: 10, not_after_minutes: "120",
      attempts: 2, between_minutes: [1], after_cycle_minutes: 240, initial_delay_minutes: 0,
      did_strategy: ["branded", "same_as_last"], calls: callsFor(2, []),
    },
    {
      name: "followup", ends_days: "90", priority_band: 100, age_decay_per_day: 0, not_after_minutes: "1440",
      attempts: 1, between_minutes: [], after_cycle_minutes: 3 * 24 * 60, initial_delay_minutes: 0,
      did_strategy: ["branded"], calls: callsFor(1, []),
    },
  ],
  total_attempts: 40,
  expire_days: "90",
  daily_calls_per_number: "6",
  daily_texts_per_lead: "2",
  end_outcome: "",
  technical_retry_minutes: 5,
  max_retries: 2,
  invalid_stops_after: 2,
  not_worked_minutes: 10,
  abandoned_counts: true,
  manual_dial_counts: true,
  inbound_counts: true,
  callback_default_minutes: 60,
  callback_kept_for_minutes: 60,
  callback_window_minutes: null,
  priority_callback: 900,
  priority_about_to_sign: 800,
  priority_mid_cycle: 700,
  priority_initial_fresh: 500,
};

const minutes = (seconds: number | null | undefined, fallback: number) =>
  seconds === null || seconds === undefined ? fallback : Math.round(seconds / 60);

/** The form for a saved body. Fields the form does not edit are carried by `bodyFromForm`. */
export function formFromBody(body: CadenceBody, name = "", slug = ""): CadenceFormValues {
  return {
    name,
    slug,
    note: "",
    phases: body.phases.map((p) => ({
      name: p.name,
      ends_days: p.ends_days_after_enrollment === null || p.ends_days_after_enrollment === undefined ? "" : String(p.ends_days_after_enrollment),
      priority_band: p.priority_band,
      age_decay_per_day: p.age_decay_per_day ?? 0,
      not_after_minutes: p.not_after_seconds === null || p.not_after_seconds === undefined ? "" : String(minutes(p.not_after_seconds, 0)),
      attempts: p.cycle.attempts,
      between_minutes: (p.cycle.between_attempts_seconds ?? []).map((s) => minutes(s, 1)),
      after_cycle_minutes: minutes(p.cycle.after_cycle_seconds, 240),
      initial_delay_minutes: minutes(p.cycle.initial_delay_seconds ?? 0, 0),
      did_strategy: p.cycle.did_strategy?.length ? [...p.cycle.did_strategy] : ["branded"],
      calls: callsFor(p.cycle.attempts, Array.from({ length: p.cycle.attempts }, (_, k) => callFromBody(p.cycle.calls?.[k]))),
      ...(p.prefer_window ? { prefer_window: true } : {}),
    })),
    total_attempts: body.limits?.total_attempts ?? 40,
    expire_days: body.limits?.expire_days_after_enrollment === null || body.limits?.expire_days_after_enrollment === undefined ? "" : String(body.limits.expire_days_after_enrollment),
    daily_calls_per_number: body.limits?.daily_calls_per_number === null || body.limits?.daily_calls_per_number === undefined ? "" : String(body.limits.daily_calls_per_number),
    // Absent is the server's own two a day, and so is null: a cadence saved when texts could have
    // no cap reads as two, as the server reads it.
    daily_texts_per_lead: String(body.limits?.daily_texts_per_lead ?? DEFAULT_DAILY_TEXTS),
    end_outcome: body.on_end?.outcome ?? "",
    technical_retry_minutes: minutes(body.technical?.retry_seconds, 5),
    max_retries: body.technical?.max_retries ?? 2,
    invalid_stops_after: body.technical?.invalid_stops_after ?? 2,
    not_worked_minutes: minutes(body.on_outcome?.not_worked_seconds, 10),
    abandoned_counts: body.on_outcome?.abandoned_counts ?? true,
    manual_dial_counts: body.on_outcome?.manual_dial_counts ?? true,
    inbound_counts: body.on_outcome?.inbound_counts ?? true,
    callback_default_minutes: minutes(body.on_outcome?.callback_default_seconds, 60),
    callback_kept_for_minutes: minutes(body.on_outcome?.callback_kept_for_seconds, 60),
    // Absent and null both read as none: a version saved before the window had one.
    callback_window_minutes:
      body.on_outcome?.callback_window_seconds === null || body.on_outcome?.callback_window_seconds === undefined
        ? null
        : minutes(body.on_outcome.callback_window_seconds, 0),
    priority_callback: body.priority?.callback ?? 900,
    priority_about_to_sign: body.priority?.about_to_sign ?? 800,
    priority_mid_cycle: body.priority?.mid_cycle ?? 700,
    priority_initial_fresh: body.priority?.initial_fresh ?? 500,
  };
}

/**
 * The body core-api validates, from the form. `previous` is the body being edited: what the form
 * does not show (the opening block, the technical cause lists, the fields this build does not
 * run) rides along unchanged, so an edit of a wait never drops an opening somebody typed as JSON.
 */
export function bodyFromForm(v: CadenceFormValues, previous?: CadenceBody | null): CadenceBody {
  const phases: CadencePhase[] = v.phases.map((p, i) => {
    const kept = previous?.phases?.[i];
    return {
      name: p.name.trim(),
      ends_days_after_enrollment: p.ends_days.trim() === "" ? null : Number(p.ends_days),
      priority_band: p.priority_band,
      age_decay_per_day: p.age_decay_per_day,
      ...(kept?.priority_floor !== undefined && kept?.priority_floor !== null ? { priority_floor: kept.priority_floor } : {}),
      not_after_seconds: p.not_after_minutes.trim() === "" ? null : Number(p.not_after_minutes) * 60,
      // Written only by a phase that turns it on, or one that already said: a cadence that never
      // did reads as it did, for a core-api that predates the switch too (ADR-156).
      ...(p.prefer_window || kept?.prefer_window !== undefined ? { prefer_window: Boolean(p.prefer_window) } : {}),
      cycle: {
        attempts: p.attempts,
        between_attempts_seconds: p.between_minutes.slice(0, Math.max(0, p.attempts - 1)).map((m) => m * 60),
        after_cycle_seconds: p.after_cycle_minutes * 60,
        initial_delay_seconds: p.initial_delay_minutes * 60,
        did_strategy: p.did_strategy.slice(0, p.attempts),
        ...(kept?.cycle?.voicemail_after_attempt !== undefined && kept?.cycle?.voicemail_after_attempt !== null
          ? { voicemail_after_attempt: kept.cycle.voicemail_after_attempt }
          : {}),
        // Only a cadence that uses a call's options writes them: one at its defaults reads as before.
        ...(callsFor(p.attempts, p.calls).some((c) => !isDefaultCall(c))
          ? { calls: callsFor(p.attempts, p.calls).map(callToBody) }
          : {}),
      },
    };
  });
  return {
    clock: "host",
    phases,
    ...(previous?.opening ? { opening: previous.opening } : {}),
    limits: {
      total_attempts: v.total_attempts,
      expire_days_after_enrollment: v.expire_days.trim() === "" ? null : Number(v.expire_days),
      daily_calls_per_number: v.daily_calls_per_number.trim() === "" ? null : Number(v.daily_calls_per_number),
      // Written only by a cadence that texts, or one that already said it: a cadence of calls
      // reads as it did, for a core-api that predates texts too.
      ...(hasTexts(v) || previous?.limits?.daily_texts_per_lead !== undefined
        ? { daily_texts_per_lead: Number(v.daily_texts_per_lead) }
        : {}),
    },
    technical: {
      ...(previous?.technical ?? {}),
      retry_seconds: v.technical_retry_minutes * 60,
      max_retries: v.max_retries,
      invalid_stops_after: v.invalid_stops_after,
    },
    on_outcome: {
      ...(previous?.on_outcome ?? {}),
      not_worked_seconds: v.not_worked_minutes * 60,
      abandoned_counts: v.abandoned_counts,
      manual_dial_counts: v.manual_dial_counts,
      inbound_counts: v.inbound_counts,
      callback_default_seconds: v.callback_default_minutes * 60,
      callback_kept_for_seconds: v.callback_kept_for_minutes * 60,
      callback_window_seconds: v.callback_window_minutes === null ? null : v.callback_window_minutes * 60,
    },
    priority: {
      ...(previous?.priority ?? {}),
      callback: v.priority_callback,
      about_to_sign: v.priority_about_to_sign,
      mid_cycle: v.priority_mid_cycle,
      initial_fresh: v.priority_initial_fresh,
    },
    // Written only by a cadence that sets an outcome, or one that already said: a cadence that
    // never did reads as it did, for a core-api that predates the setting too (ADR-155).
    ...(v.end_outcome !== "" || previous?.on_end !== undefined
      ? { on_end: { ...(previous?.on_end ?? {}), outcome: v.end_outcome === "" ? null : v.end_outcome } }
      : {}),
  };
}

const wholeIn = (n: unknown, [min, max]: readonly [number, number]) =>
  typeof n === "number" && Number.isInteger(n) && n >= min && n <= max;

/**
 * Why a text's words would be refused, or null when they would be taken (`cadence_body` and
 * `cadence_texts.problems` in platform, the first of them). `known` is every field a text may
 * use; while it has not loaded the names are not judged, so a text is never called wrong for a
 * list that is still on its way.
 */
export function textProblem(text: string, known?: ReadonlySet<string>): string | null {
  if (text.trim() === "") return "Write the text";
  if (text.length > TEXT_MAX) return `At most ${TEXT_MAX.toLocaleString()} characters`;
  const pieces = scriptPieces(text);
  if (pieces.some((p) => p.kind === "text" && /[{}]/.test(p.text))) {
    return "A field is written {{name}} or {{name|fallback words}}: check the braces";
  }
  for (const piece of pieces) {
    if (piece.kind !== "field") continue;
    if (piece.name.startsWith("rep.")) return `${piece.name}: no rep is on a text`;
    if (known && !known.has(piece.name)) return `${piece.name} is not a field a text can use`;
    if (piece.fallback !== null && piece.fallback.length > TEXT_FALLBACK_MAX) {
      return `The fallback words of ${piece.name} are at most ${TEXT_FALLBACK_MAX} characters`;
    }
  }
  if (!pieces.some((p) => p.kind === "text" && p.text.trim() !== "")) {
    return "A text needs words of its own around its fields";
  }
  return null;
}

/** A step's own problem: a text's words, then its Spanish words when it has any. A call has none. */
export function stepProblem(step: CallValues, known?: ReadonlySet<string>): string | null {
  if (!isText(step)) return null;
  const own = textProblem(step.text, known);
  if (own) return own;
  if (step.text_es.trim() === "") return null;
  const spanish = textProblem(step.text_es, known);
  return spanish ? `Spanish: ${spanish}` : null;
}

/** Whether any step of the cadence is a text. */
export function hasTexts(v: Pick<CadenceFormValues, "phases">): boolean {
  return v.phases.some((p) => callsFor(p.attempts, p.calls).some(isText));
}

/** Whether any step of the cadence is a call. */
export function hasCalls(v: Pick<CadenceFormValues, "phases">): boolean {
  return v.phases.some((p) => callsFor(p.attempts, p.calls).some((c) => !isText(c)));
}

/** Whether no step of the phase's cycle is a call. */
export function textsOnly(phase: Pick<PhaseValues, "attempts" | "calls">): boolean {
  return callsFor(phase.attempts, phase.calls).every(isText);
}

/**
 * What the server would refuse, said on the field first. Keys are dotted paths into the form
 * (`phases.0.attempts`, and `phases.0.calls.1.text` for a text's words); an empty object is a
 * form the server will take. `textFields` is every field a text may use, once it has loaded.
 */
export function formProblems(v: CadenceFormValues, textFields?: ReadonlySet<string>): Record<string, string> {
  const out: Record<string, string> = {};
  const b = CADENCE_BOUNDS;
  if (!v.name.trim()) out.name = "Give the cadence a name";
  if (!CADENCE_SLUG_RE.test(v.slug)) out.slug = "Lowercase letters, digits and underscores, 2 to 80 long, starting with a letter";
  if (v.phases.length < b.phases[0] || v.phases.length > b.phases[1]) out.phases = `Between ${b.phases[0]} and ${b.phases[1]} phases`;
  let previous = 0;
  const names = new Set<string>();
  v.phases.forEach((p, i) => {
    const at = (field: string) => `phases.${i}.${field}`;
    if (!PHASE_NAME_RE.test(p.name.trim())) out[at("name")] = "Lowercase letters, digits and underscores, up to 24";
    if (names.has(p.name.trim())) out[at("name")] = "Phase names are unique";
    names.add(p.name.trim());
    const last = i === v.phases.length - 1;
    if (p.ends_days.trim() === "") {
      if (!last) out[at("ends_days")] = "Only the last phase may be open-ended";
    } else {
      const days = Number(p.ends_days);
      if (!wholeIn(days, b.expire_days)) out[at("ends_days")] = `A whole number of days from ${b.expire_days[0]} to ${b.expire_days[1]}`;
      else if (days <= previous) out[at("ends_days")] = "Phases end in increasing order";
      previous = days;
    }
    if (!wholeIn(p.priority_band, b.priority)) out[at("priority_band")] = `From ${b.priority[0]} to ${b.priority[1]}`;
    if (!wholeIn(p.age_decay_per_day, b.decay)) out[at("age_decay_per_day")] = `From ${b.decay[0]} to ${b.decay[1]} a day`;
    if (p.not_after_minutes.trim() !== "" && !wholeIn(Number(p.not_after_minutes), b.wait_minutes)) {
      out[at("not_after_minutes")] = `A whole number of minutes from ${b.wait_minutes[0]} to ${b.wait_minutes[1].toLocaleString()}, or empty for never`;
    }
    if (!wholeIn(p.attempts, b.attempts)) out[at("attempts")] = `From ${b.attempts[0]} to ${b.attempts[1]} calls`;
    if (p.attempts > 1 && p.between_minutes.length === 0) out[at("between_minutes")] = "A cycle of more than one call needs a wait between the calls";
    if (p.between_minutes.length > Math.max(0, p.attempts - 1)) out[at("between_minutes")] = `At most ${p.attempts - 1} wait${p.attempts - 1 === 1 ? "" : "s"} between ${p.attempts} calls`;
    if (p.between_minutes.some((m) => !wholeIn(m, b.between_minutes))) out[at("between_minutes")] = `Each wait is right away or up to ${b.between_minutes[1].toLocaleString()} minutes`;
    if (!wholeIn(p.after_cycle_minutes, b.wait_minutes)) out[at("after_cycle_minutes")] = `From ${b.wait_minutes[0]} to ${b.wait_minutes[1].toLocaleString()} minutes`;
    if (!wholeIn(p.initial_delay_minutes, b.initial_delay_minutes)) out[at("initial_delay_minutes")] = `From 0 to ${b.initial_delay_minutes[1].toLocaleString()} minutes`;
    if (p.did_strategy.length === 0 || p.did_strategy.length > p.attempts) out[at("did_strategy")] = "One strategy per call of the cycle, at most";
    if (p.calls.some((c) => c.redials.length > MAX_REDIALS)) out[at("calls")] = `At most ${MAX_REDIALS} redials a call`;
    if (wholeIn(p.attempts, b.attempts)) {
      callsFor(p.attempts, p.calls).forEach((step, k) => {
        const problem = stepProblem(step, textFields);
        if (problem) out[at(`calls.${k}.text`)] = problem;
      });
      if (textsOnly(p) && wholeIn(p.after_cycle_minutes, b.wait_minutes) && p.after_cycle_minutes < b.text_cycle_minutes[0]) {
        out[at("after_cycle_minutes")] = "A round of only texts starts again after an hour at least";
      }
    }
  });
  if (!wholeIn(v.total_attempts, b.total_attempts)) out.total_attempts = `From ${b.total_attempts[0]} to ${b.total_attempts[1]} attempts`;
  if (v.expire_days.trim() !== "" && !wholeIn(Number(v.expire_days), b.expire_days)) out.expire_days = `A whole number of days from ${b.expire_days[0]} to ${b.expire_days[1]}, or empty for never`;
  if (v.daily_calls_per_number.trim() !== "" && !wholeIn(Number(v.daily_calls_per_number), b.daily_calls_per_number)) out.daily_calls_per_number = `A whole number from ${b.daily_calls_per_number[0]} to ${b.daily_calls_per_number[1]} calls to a number in a day, or empty for no cap`;
  if (hasTexts(v) && !wholeIn(Number(v.daily_texts_per_lead), b.daily_texts_per_lead)) out.daily_texts_per_lead = `A whole number from ${b.daily_texts_per_lead[0]} to ${b.daily_texts_per_lead[1]} texts to a number in a day`;
  if (!wholeIn(v.technical_retry_minutes, b.technical_retry_minutes)) out.technical_retry_minutes = `From ${b.technical_retry_minutes[0]} to ${b.technical_retry_minutes[1]} minutes`;
  if (!wholeIn(v.max_retries, b.max_retries)) out.max_retries = `From ${b.max_retries[0]} to ${b.max_retries[1]}`;
  if (!wholeIn(v.invalid_stops_after, b.invalid_stops_after)) out.invalid_stops_after = `From ${b.invalid_stops_after[0]} to ${b.invalid_stops_after[1]}`;
  if (!wholeIn(v.not_worked_minutes, b.not_worked_minutes)) out.not_worked_minutes = `From ${b.not_worked_minutes[0]} to ${b.not_worked_minutes[1]} minutes`;
  if (!wholeIn(v.callback_default_minutes, b.wait_minutes)) out.callback_default_minutes = `From ${b.wait_minutes[0]} to ${b.wait_minutes[1].toLocaleString()} minutes`;
  if (!wholeIn(v.callback_kept_for_minutes, b.callback_kept_for_minutes)) out.callback_kept_for_minutes = `From ${b.callback_kept_for_minutes[0]} to ${b.callback_kept_for_minutes[1].toLocaleString()} minutes`;
  if (v.callback_window_minutes !== null && !wholeIn(v.callback_window_minutes, b.callback_window_minutes)) out.callback_window_minutes = `From ${b.callback_window_minutes[0]} to ${b.callback_window_minutes[1].toLocaleString()} minutes, or none`;
  for (const key of ["priority_callback", "priority_about_to_sign", "priority_mid_cycle", "priority_initial_fresh"] as const) {
    if (!wholeIn(v[key], b.priority)) out[key] = `From ${b.priority[0]} to ${b.priority[1]}`;
  }
  return out;
}

/** The waits list resized to a cycle's calls: one fewer than the calls, the last wait repeated. */
export function waitsFor(attempts: number, waits: number[]): number[] {
  const wanted = Math.max(0, attempts - 1);
  if (waits.length >= wanted) return waits.slice(0, wanted);
  const last = waits[waits.length - 1] ?? 1;
  return [...waits, ...Array.from({ length: wanted - waits.length }, () => last)];
}

// --- the flow editor's arithmetic ------------------------------------------------------------------

/** The units a wait is shown in. The form keeps minutes; these only choose how a chip reads. */
export const DURATION_UNITS = ["min", "h", "d"] as const;
export type DurationUnit = (typeof DURATION_UNITS)[number];
export const MINUTES_PER: Record<DurationUnit, number> = { min: 1, h: 60, d: 24 * 60 };

/** The largest unit that divides the minutes evenly: 240 → 4 h, 4320 → 3 d, 90 → 90 min. */
export function durationParts(minutes: number): { value: number; unit: DurationUnit } {
  if (!Number.isFinite(minutes)) return { value: Number.NaN, unit: "min" };
  if (minutes <= 0) return { value: minutes, unit: "min" };
  for (const unit of ["d", "h"] as const) {
    if (minutes % MINUTES_PER[unit] === 0) return { value: minutes / MINUTES_PER[unit], unit };
  }
  return { value: minutes, unit: "min" };
}

/** The strategy list resized to a cycle's calls, the last strategy repeated. */
export function strategiesFor(attempts: number, strategies: string[]): string[] {
  const wanted = Math.max(1, attempts);
  if (strategies.length >= wanted) return strategies.slice(0, wanted);
  const last = strategies[strategies.length - 1] ?? "branded";
  return [...strategies, ...Array.from({ length: wanted - strategies.length }, () => last)];
}

/** One more call at the end of the cycle: it waits as long as the last gap did and uses the same
 *  number as the call before it. The cycle's first call, after nothing but texts, has no call
 *  before it and uses the campaign's own number. */
export function withCall(phase: PhaseValues): PhaseValues {
  const attempts = phase.attempts + 1;
  const strategies = strategiesFor(phase.attempts, phase.did_strategy);
  const called = phase.attempts >= 1 && callsFor(phase.attempts, phase.calls).some((c) => !isText(c));
  return {
    ...phase,
    attempts,
    between_minutes: waitsFor(attempts, phase.between_minutes),
    did_strategy: [...strategies, called ? "same_as_last" : "branded"],
    calls: callsFor(attempts, phase.calls),
  };
}

/** One more step at the end of the cycle that is a text (ADR-135 in platform): it waits as long
 *  as the last gap did, and its words are the admin's to write. */
export function withText(phase: PhaseValues): PhaseValues {
  const next = withCall(phase);
  const calls = [...next.calls];
  calls[calls.length - 1] = { ...NEW_TEXT };
  return { ...next, calls };
}

/** The cycle with step `index` one place earlier (`by` -1) or later (+1). The step takes its own
 *  options and its number with it; the waits stay where they are, between the places. */
export function withStepMoved(phase: PhaseValues, index: number, by: -1 | 1): PhaseValues {
  const to = index + by;
  if (index < 0 || to < 0 || index >= phase.attempts || to >= phase.attempts) return phase;
  const calls = callsFor(phase.attempts, phase.calls);
  const strategies = strategiesFor(phase.attempts, phase.did_strategy);
  [calls[index], calls[to]] = [calls[to]!, calls[index]!];
  [strategies[index], strategies[to]] = [strategies[to]!, strategies[index]!];
  return { ...phase, calls, did_strategy: strategies };
}

/** The cycle without its call `index`; the gap before it goes with it (the first call keeps no
 *  gap, so removing it drops the gap after it). A cycle keeps at least one call. */
export function withoutCall(phase: PhaseValues, index: number): PhaseValues {
  if (phase.attempts <= 1) return phase;
  const waits = waitsFor(phase.attempts, phase.between_minutes);
  const strategies = strategiesFor(phase.attempts, phase.did_strategy);
  const gap = Math.max(0, index - 1);
  return {
    ...phase,
    attempts: phase.attempts - 1,
    between_minutes: waits.filter((_, i) => i !== gap),
    did_strategy: strategies.filter((_, i) => i !== index),
    calls: callsFor(phase.attempts, phase.calls).filter((_, i) => i !== index),
  };
}

/** The phase with call `index`'s options replaced. */
export function withCallOptions(phase: PhaseValues, index: number, options: Partial<CallValues>): PhaseValues {
  const calls = callsFor(phase.attempts, phase.calls);
  calls[index] = { ...calls[index]!, ...options };
  return { ...phase, calls };
}

/** Call `index` with `count` redials: the ones it has are kept, a new one comes from the same
 *  number and hangs up on a voicemail. */
export function withRedials(phase: PhaseValues, index: number, count: number): PhaseValues {
  const call = callsFor(phase.attempts, phase.calls)[index]!;
  const n = Math.max(0, Math.min(MAX_REDIALS, Math.round(count)));
  const redials = Array.from({ length: n }, (_, k) => ({ ...(call.redials[k] ?? NEW_REDIAL) }));
  return withCallOptions(phase, index, { redials });
}

// --- a call's options, in words ---------------------------------------------------------------------

/** "call 2", "calls 2 and 3", "calls 1, 3 and 4": calls by their place, from 0. */
export function callsWords(indexes: readonly number[]): string {
  const n = indexes.map((i) => String(i + 1));
  if (n.length === 0) return "no call";
  if (n.length === 1) return `call ${n[0]}`;
  return `calls ${n.slice(0, -1).join(", ")} and ${n[n.length - 1]}`;
}

/** Steps by what each is and its place, from 0: "call 2", "texts 1 and 3", "text 1 and call 2".
 *  All calls reads as `callsWords` does. */
export function stepsWords(steps: readonly Pick<CallValues, "channel">[], indexes: readonly number[]): string {
  const texts = indexes.filter((i) => steps[i] && isText(steps[i]!));
  if (texts.length === 0) return callsWords(indexes);
  const join = (names: string[]) => (names.length === 1 ? names[0]! : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`);
  if (texts.length === indexes.length) {
    return indexes.length === 1 ? `text ${indexes[0]! + 1}` : `texts ${join(indexes.map((i) => String(i + 1)))}`;
  }
  return join(indexes.map((i) => `${steps[i] && isText(steps[i]!) ? "text" : "call"} ${i + 1}`));
}

/** A text's words on one line, cut to fit its row: what the admin wrote, fields as written. */
export function textSnippet(text: string, max = 72): string {
  const line = text.replace(/\s+/g, " ").trim();
  return line.length > max ? `${line.slice(0, max - 1).trimEnd()}…` : line;
}

/**
 * A text as a lead would read it, with each field's example in its place: what the editor shows
 * under the words. A field with no example says its fallback words, else its name in brackets, so
 * a field the list does not know is seen and not hidden. The server fills the real one
 * (`greetings.render(written=True)` in platform); this is for reading a draft.
 */
export function textPreview(text: string, fields: readonly Pick<GreetingField, "name" | "example">[] = []): string {
  const example = new Map(fields.map((f) => [f.name, f.example]));
  const filled = scriptPieces(text)
    .map((p) => (p.kind === "text" ? p.text : example.get(p.name) || p.fallback || `[${p.name}]`))
    .join("");
  return filled
    .split(/\r?\n/)
    .map((line) => line.replace(/[ \t]+/g, " ").replace(/\s+([,.;:!?])/g, "$1").trim())
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

export interface CallTag {
  kind: "redials" | "voicemail" | "once" | "spanish";
  text: string;
}

/** What a call does besides being placed, as the tags on its row: only what differs from the
 *  defaults, so a plain call has none. A text says only whether it is made once and whether it
 *  has Spanish words. */
export function callTags(call: CallValues): CallTag[] {
  const tags: CallTag[] = [];
  if (isText(call)) {
    if (call.text_es.trim()) tags.push({ kind: "spanish", text: "Spanish too" });
    if (call.only_once) tags.push({ kind: "once", text: "Only once" });
    return tags;
  }
  const n = call.redials.length;
  if (n > 0) tags.push({ kind: "redials", text: `+${n} redial${n === 1 ? "" : "s"}` });
  const leaving = call.redials.flatMap((r, k) => (leavesMessage(r.on_voicemail) ? [k + 1] : []));
  // Said only when every message of the call is the dialer's own: a mix is just "voicemails".
  const choices = [call.on_voicemail, ...call.redials.map((r) => r.on_voicemail)].filter(leavesMessage);
  const auto = choices.length > 0 && choices.every((c) => c === "leave_message_auto");
  if (leavesMessage(call.on_voicemail)) {
    const text = leaving.length ? "Leaves voicemails" : "Leaves a voicemail";
    tags.push({ kind: "voicemail", text: auto ? `${text} automatically` : text });
  } else if (leaving.length) {
    const where = `on redial${leaving.length === 1 ? "" : "s"} ${leaving.length === 1 ? leaving[0] : `${leaving.slice(0, -1).join(", ")} and ${leaving[leaving.length - 1]}`}`;
    tags.push({ kind: "voicemail", text: auto ? `Automatic voicemail ${where}` : `Voicemail ${where}` });
  }
  if (call.only_once) tags.push({ kind: "once", text: "Only once" });
  return tags;
}

export type Repeats =
  | { kind: "every" }
  | { kind: "some"; calls: number[] }
  | { kind: "once" };

/** What a round that starts again makes: every call, some of them, or nothing (the phase runs
 *  one round). */
export function repeats(phase: PhaseValues): Repeats {
  const calls = callsFor(phase.attempts, phase.calls);
  const again = calls.flatMap((c, k) => (c.only_once ? [] : [k]));
  if (again.length === calls.length) return { kind: "every" };
  if (again.length === 0) return { kind: "once" };
  return { kind: "some", calls: again };
}

/** A call and its redials as calls to one number, the most of any call in the cadence. */
export function largestRun(values: CadenceFormValues): { phase: number; call: number; calls: number } | null {
  let best: { phase: number; call: number; calls: number } | null = null;
  values.phases.forEach((p, i) =>
    callsFor(p.attempts, p.calls).forEach((c, k) => {
      const calls = 1 + c.redials.length;
      if (c.redials.length > 0 && (!best || calls > best.calls)) best = { phase: i, call: k, calls };
    }),
  );
  return best;
}

/** Said under the daily cap when a call and its redials do not fit in it: each redial counts. */
export function capProblem(values: CadenceFormValues): string | null {
  const cap = values.daily_calls_per_number.trim();
  const run = largestRun(values);
  if (!cap || !run || run.calls <= Number(cap)) return null;
  const redials = run.calls - 1;
  return `A call and its ${redials} redial${redials === 1 ? "" : "s"} are ${run.calls} calls, more than the daily cap of ${cap}: the cap cuts the redials short.`;
}

/** The problems of one phase, for the list under its card. */
export function phaseProblems(problems: Record<string, string>, index: number): string[] {
  const prefix = `phases.${index}.`;
  return Object.entries(problems)
    .filter(([key]) => key.startsWith(prefix))
    .map(([, message]) => message);
}
